import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Users, Building2, LogOut, Plus } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'

interface MobileLayoutProps {
  title: string
  children: React.ReactNode
  /** Élément optionnel affiché à droite dans la barre du haut */
  headerAction?: React.ReactNode
  /** Si fourni, affiche un bouton flottant "+" en bas à droite */
  onAdd?: () => void
}

export default function MobileLayout({ title, children, headerAction, onAdd }: MobileLayoutProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout } = useAuthStore()

  const isContacts = location.pathname.startsWith('/contacts')
  const isCompanies = location.pathname.startsWith('/companies')

  return (
    // h-[100dvh] : dynamic viewport height — s'ajuste quand la barre d'adresse
    // Safari se replie/déplie, contrairement à h-screen (100vh) qui inclut
    // l'espace masqué par la barre du navigateur.
    <div className="flex flex-col h-[100dvh] bg-white">
      {/* Barre du haut */}
      <header className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-gray-100 shrink-0">
        <h1 className="text-xl font-bold text-gray-900">{title}</h1>
        <div className="flex items-center gap-1">
          {headerAction}
          <button
            onClick={() => {
              logout()
              navigate('/login')
            }}
            className="p-2 text-gray-400 hover:text-gray-600"
            title="Déconnexion"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Contenu */}
      <main className="flex-1 overflow-y-auto min-h-0">{children}</main>

      {/* Bouton flottant "+" — position ajustée à la hauteur onglets + safe-area home indicator */}
      {onAdd && (
        <button
          onClick={onAdd}
          style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom))' }}
          className="fixed right-4 z-20 w-14 h-14 rounded-full bg-blue-600 text-white shadow-lg flex items-center justify-center active:bg-blue-700"
          title="Ajouter"
        >
          <Plus className="w-7 h-7" />
        </button>
      )}

      {/* Barre d'onglets en bas — pb safe-area pour rester au-dessus de la
          barre d'adresse Safari et du home indicator iOS. */}
      <nav
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        className="grid grid-cols-2 border-t border-gray-100 shrink-0 bg-white"
      >
        <button
          onClick={() => navigate('/contacts')}
          className={`flex flex-col items-center gap-0.5 py-2.5 transition-colors ${
            isContacts ? 'text-blue-600' : 'text-gray-400'
          }`}
        >
          <Users className="w-5 h-5" />
          <span className={`text-[11px] ${isContacts ? 'font-semibold' : 'font-medium'}`}>Contacts</span>
        </button>
        <button
          onClick={() => navigate('/companies')}
          className={`flex flex-col items-center gap-0.5 py-2.5 transition-colors ${
            isCompanies ? 'text-blue-600' : 'text-gray-400'
          }`}
        >
          <Building2 className="w-5 h-5" />
          <span className={`text-[11px] ${isCompanies ? 'font-semibold' : 'font-medium'}`}>Entreprises</span>
        </button>
      </nav>
    </div>
  )
}
